// COMPLIANCE: STATUS_COLORS violation detected - requires manual review
import { cn } from '@/lib/utils';
// COMPLIANCE: STATUS_COLORS violation detected - requires manual review
import { useState, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useLocalizationEdit } from '@/contexts/LocalizationEditContext';
import { Check, X, RotateCcw, Sparkles, Loader2 } from 'lucide-react';
import { FORM_CONTROLS, TYPOGRAPHY, RESPONSIVE, SPACING } from '@/design-system';
import { useLocale } from '@/contexts/LocaleContext';

interface InlineEditPopoverProps {
  namespace: string;
  localizationKey: string;
  currentValue: string;
  originalValue: string;
  children: React.ReactNode;
}

export function InlineEditPopover({
  namespace,
  localizationKey,
  currentValue,
  originalValue,
  children,
}: InlineEditPopoverProps) {
  const [open, setOpen] = useState(false);
  const [editValue, setEditValue] = useState(currentValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const { 
    setPendingEdit, 
    clearPendingEdit, 
    pendingEdits, 
    suggestions, 
    applySuggestion, 
    rejectSuggestion,
    regenerateSingleKey,
    regeneratingKey,
  } = useLocalizationEdit();
  
  const compositeKey = `${namespace}.${localizationKey}`;
  const hasPendingEdit = pendingEdits.has(compositeKey);
  const suggestion = suggestions.get(compositeKey);
  const isRegenerating = regeneratingKey === compositeKey;
  
  const handleApply = () => {
    if (editValue !== originalValue) {
      setPendingEdit(compositeKey, editValue);
    } else {
      clearPendingEdit(compositeKey);
    }
    setOpen(false);
  };
  
  const handleReset = () => {
    setEditValue(originalValue);
    clearPendingEdit(compositeKey);
  };
  
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setEditValue(currentValue);
    }
  };

  const handleRegenerate = async () => {
    const result = await regenerateSingleKey(namespace, localizationKey, originalValue);
    if (result) {
      // Auto-populate edit field with the suggestion
      setEditValue(result.suggested);
    }
  };
  
  // Handle click - only open popover if Cmd/Ctrl is held, otherwise let click propagate
  const handleTriggerClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
    }
    // Otherwise, let click propagate to parent Link/Button for normal navigation
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild onClick={handleTriggerClick}>
        {children}
      </PopoverTrigger>
      <PopoverContent 
        className={cn(SPACING.cardPadding, 'w-96')} 
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className={cn(SPACING.contentContainer)}>
          {/* Key info */}
          <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
            <Badge variant="outline" className="font-mono text-xs">
              {namespace}
            </Badge>
            <span className={cn(TYPOGRAPHY.caption, 'font-mono truncate flex-1')}>
              {localizationKey}
            </span>
          </div>
          
          {/* Original value */}
          <div>
            <Label className={cn(TYPOGRAPHY.caption)}>Original</Label>
            <p className="text-sm bg-muted/50 p-2 rounded mt-1 break-words">
              {originalValue}
            </p>
          </div>

          {/* AI Regenerate button */}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleRegenerate}
            disabled={isRegenerating}
          >
            {isRegenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Regenerate with AI
              </>
            )}
          </Button>
          
          {/* AI Suggestion if present */}
          {suggestion && (
            <div className="border border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800 rounded p-2">
              <div className={cn(RESPONSIVE.flexRowBetween, 'mb-1')}>
                <Label className="text-xs text-success dark:text-green-400">AI Suggestion</Label>
                <Badge variant="secondary" className="text-xs">
                  {Math.round(suggestion.confidence * 100)}% confidence
                </Badge>
              </div>
              <p className="text-sm text-green-800 dark:text-green-300 mb-2">{suggestion.suggested}</p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  className={FORM_CONTROLS.button.compactAction}
                  onClick={() => {
                    applySuggestion(compositeKey);
                    setEditValue(suggestion.suggested);
                  }}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Accept
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className={FORM_CONTROLS.button.compactAction}
                  onClick={() => rejectSuggestion(compositeKey)}
                >
                  <X className="h-3 w-3 mr-1" />
                  Reject
                </Button>
              </div>
            </div>
          )}
          
          {/* Edit field */}
          <div>
            <Label className="text-xs">Edit Value</Label>
            <Input
              ref={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="mt-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleApply();
                }
              }}
            />
          </div>
          
          {/* Actions */}
          <div className="flex justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={!hasPendingEdit && editValue === originalValue}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={editValue === currentValue}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
